import type { AccidentReportRow, MaintenanceRequestRow, TimeoffRequestRow, TruckRow } from '@routeback/core';
import { Empty } from '@routeback/ui';
import { useLiveQuery } from 'dexie-react-hooks';
import { Link } from 'react-router-dom';
import { useApp } from '../../lib/app';
import { usePendingUploadCount } from '../../lib/photos';
import { OPEN, STATUS_LABEL } from '../maintenance/maintenance';
import { TimeoffItem } from '../timeoff/TimeOffView';
import { todayIn } from '../timeoff/timeoff';
import { useMyTimeoff } from '../timeoff/useTimeoffData';
import { UniformBatchItem } from '../uniforms/UniformsView';
import { batchOpen, batches, type Batch } from '../uniforms/uniforms';
import { useMyUniformRequests } from '../uniforms/useUniformData';
import { ACCIDENT_STATUS } from '../accident/AccidentDetailView';

type Item = { kind: 'maintenance'; at: string; r: MaintenanceRequestRow } | { kind: 'timeoff'; at: string; r: TimeoffRequestRow } | { kind: 'uniform'; at: string; b: Batch } | { kind: 'accident'; at: string; r: AccidentReportRow };

/**
 * Everything I've submitted, newest first, with where it stands. Grows as
 * each request type lands (forms …).
 */
export function RequestsView() {
  const app = useApp();
  const today = todayIn(app.details.timezone);
  const waitingPhotos = usePendingUploadCount();
  const outbox = useLiveQuery(() => app.db.outbox.where('rejected').equals(0).count(), [app.db], 0);
  const maint = useLiveQuery(
    async () => (await app.db.table<MaintenanceRequestRow, string>('maintenance_requests').where('company_id').equals(app.company.id).toArray())
      .filter((r) => !r.deleted_at && r.user_id === app.userId),
    [app.db, app.company.id, app.userId],
    [] as MaintenanceRequestRow[],
  );
  const timeoff = useMyTimeoff();
  const uniforms = batches(useMyUniformRequests());
  const accidents = useLiveQuery(
    async () => (await app.db.table<AccidentReportRow, string>('accident_reports').where('company_id').equals(app.company.id).toArray())
      .filter((r) => !r.deleted_at && r.user_id === app.userId),
    [app.db, app.company.id, app.userId],
    [] as AccidentReportRow[],
  );
  const trucks = useLiveQuery(
    async () => new Map((await app.db.table<TruckRow, string>('trucks').where('company_id').equals(app.company.id).toArray()).map((t) => [t.id, t.unit_number])),
    [app.db, app.company.id],
    new Map<string, string>(),
  );
  const items: Item[] = [
    ...maint.map((r) => ({ kind: 'maintenance' as const, at: r.created_at, r })),
    ...timeoff.map((r) => ({ kind: 'timeoff' as const, at: r.created_at, r })),
    ...uniforms.map((b) => ({ kind: 'uniform' as const, at: b.at, b })),
    ...accidents.map((r) => ({ kind: 'accident' as const, at: r.created_at, r })),
  ].sort((a, b) => b.at.localeCompare(a.at));
  // Current: still waiting on someone, or approved time off that hasn't happened yet.
  const isCurrent = (i: Item) => i.kind === 'accident' ? i.r.status !== 'reviewed'
    : i.kind === 'uniform' ? batchOpen(i.b)
    : i.kind === 'maintenance' ? OPEN.includes(i.r.status)
    : i.r.status === 'pending' || (i.r.status === 'approved' && (i.r.span === 'open' || (i.r.end_date ?? '') >= today));
  const current = items.filter(isCurrent);
  const earlier = items.filter((i) => !isCurrent(i));
  const row = (i: Item) => {
    if (i.kind === 'uniform') return <UniformBatchItem key={i.b.id} b={i.b} />;
    if (i.kind === 'accident') {
      const a = i.r;
      return (
        <Link key={a.id} to={a.status === 'draft' ? '/accident' : `/requests/accident/${a.id}`} className="list-item settings-link">
          <span style={{ width: 26, textAlign: 'center' }}>🚨</span>
          <div className="grow">
            <div>Accident report{a.occurred_at ? ` · ${new Date(a.occurred_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}</div>
            <div className="muted small">{a.location || (a.status === 'draft' ? 'Tap to continue' : '')}</div>
          </div>
          <span className={'chip' + (a.status !== 'draft' ? ' on' : '')}>{ACCIDENT_STATUS[a.status]}</span>
        </Link>
      );
    }
    if (i.kind === 'timeoff') return <TimeoffItem key={i.r.id} r={i.r} />;
    const r = i.r;
    return (
      <Link key={r.id} to={`/requests/maintenance/${r.id}`} className="list-item settings-link">
        <span style={{ width: 26, textAlign: 'center' }}>🔧</span>
        <div className="grow">
          <div>Truck {trucks.get(r.truck_id ?? '') ?? '—'} · {r.category}</div>
          <div className="muted small">{r.description.slice(0, 60)}{r.description.length > 60 ? '…' : ''}</div>
        </div>
        <span className={'chip' + (r.status === 'done' ? ' on' : '')}>{STATUS_LABEL[r.status]}</span>
      </Link>
    );
  };
  return (
    <div>
      <div className="card">
        <div className="req-actions">
          <Link to="/maintenance/new" className="btn btn-primary" aria-label="Report a truck problem"><span className="req-ico" aria-hidden>🔧</span>Truck problem</Link>
          <Link to="/timeoff/new" className="btn" aria-label="Request time off"><span className="req-ico" aria-hidden>🏖</span>Time off</Link>
          <Link to="/uniforms" className="btn"><span className="req-ico" aria-hidden>👕</span>Uniforms</Link>
        </div>
      </div>
      {app.mode !== 'local' && (outbox > 0 || waitingPhotos > 0) && (
        <p className="muted small" style={{ margin: '0 14px' }}>⏳ {outbox > 0 ? `${outbox} change${outbox === 1 ? '' : 's'}` : ''}{outbox > 0 && waitingPhotos > 0 ? ' and ' : ''}{waitingPhotos > 0 ? `${waitingPhotos} photo${waitingPhotos === 1 ? '' : 's'}` : ''} waiting for signal — they'll send automatically.</p>
      )}
      <div className="section-title">Current</div>
      {current.length ? <div className="list">{current.map(row)}</div> : <div className="card"><Empty>Nothing open.</Empty></div>}
      {earlier.length > 0 && (<><div className="section-title">Earlier</div><div className="list">{earlier.slice(0, 30).map(row)}</div></>)}
      <p className="muted small" style={{ margin: '14px' }}>Forms will show here too as they're added.</p>
    </div>
  );
}
